/**
 * Date formatting for the LHDN submission payload.
 */

/** `YYYY-MM-DD`, the format LHDN expects for every date field. */
const LHDN_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Placeholders the import pipeline uses for "no value". */
const BLANK_TOKENS = new Set(["", "na", "n/a", "null", "undefined", "-"]);

/**
 * Formats a date-ish value as `YYYY-MM-DD`, or `null`
 */
export const toLhdnDate = (
  value: string | Date | null | undefined,
): string | null => {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : value.toISOString().slice(0, 10);
  }

  const trimmed = value.trim();
  if (BLANK_TOKENS.has(trimmed.toLowerCase())) return null;

  if (LHDN_DATE_PATTERN.test(trimmed)) return trimmed;

  const isoDatePart = trimmed.slice(0, 10);
  if (
    LHDN_DATE_PATTERN.test(isoDatePart) &&
    !Number.isNaN(Date.parse(trimmed))
  ) {
    return isoDatePart;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime())
    ? null
    : parsed.toISOString().slice(0, 10);
};

/** `HH:mm:ssZ`, the format LHDN expects for every time field. */
const LHDN_TIME_PATTERN = /^\d{2}:\d{2}:\d{2}Z$/;

/**
 * Formats a time-ish value as `HH:mm:ssZ`, or `null`
 */
export const toLhdnTime = (
  value: string | Date | null | undefined,
): string | null => {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : `${value.toISOString().slice(11, 19)}Z`;
  }

  const trimmed = value.trim();
  if (BLANK_TOKENS.has(trimmed.toLowerCase())) return null;
  if (LHDN_TIME_PATTERN.test(trimmed)) return trimmed;

  const bare = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
  if (bare) {
    const [, hh, mm, ss] = bare;
    return `${hh}:${mm}:${ss ?? "00"}Z`;
  }

  if (!Number.isNaN(Date.parse(trimmed))) {
    return `${new Date(trimmed).toISOString().slice(11, 19)}Z`;
  }

  return null;
};
